import React from 'react';
import _ from 'lodash';

import {
  Box,
  Button,
  IconButton,
  Stack,
  styled
} from '@mui/material';
import {
  activityTemplates,
  PermissionWall,
  routeDirectory,
  SingleOrderRecordNavigator
} from 'modules';
import {
  useNavigate,
  useParams,
  useSearchParams
} from 'react-router-dom';
import {
  ArrowBack as MuiArrowBack,
  Download
} from '@mui/icons-material';

import { modalUtil } from 'iron-forge-react-modules';
import { ExportAsnModal } from 'modules';
import * as OrderListPane from './OrderListPane/OrderListPane';
import * as OrderDetailPane from './OrderDetailPane/OrderDetailPane';
import * as ReturnAuthorizationPane from './ReturnAuthorizationPane/ReturnAuthorizationPane';
import * as dataService from './dataService';



const ArrowBack = styled(MuiArrowBack)(({ theme }) => ({
  color: theme.palette.primary.main
}));

const ButtonBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1)
}));

const ContainerBox = styled(Box)(() => ({
  display: 'flex',
  height: '100%'
}));

const DetailPaneBox = styled(Box)(({ theme }) => ({
  flex: 1,
  paddingLeft: theme.spacing(2),
  marginRight: theme.spacing(2),
  display: 'flex',
  flexDirection: 'column',
  overflowY: 'scroll',
  paddingTop: theme.spacing(2)
}));



export const StoreDetailActivity: React.FC = () => {
  const params = useParams<{ storeId: string; }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedOrderId = searchParams.get('orderId') ?? '';
  const [selectedRecordId, setSelectedRecordId] = React.useState('');
  const navigate = useNavigate();
  const queryTuple = dataService.useQuery();
  const [fnDownloadReport] = dataService.useExportInventoryReportQuery(params.storeId ?? '');
  const {
    modalType2IsOpen,
    setModalIsOpen
  } = modalUtil.useModalTrackers([
    'exportAsn'
  ]);



  React.useEffect(
    () => {
      setSelectedRecordId(selectedOrderId);
    },
    [selectedOrderId]);

  React.useEffect(
    () => {
      if (!params.storeId) {
        navigate(routeDirectory.partner.stores.path());
      }
    },
    [
      navigate,
      params.storeId
    ]
  );



  function handleClickOrders() {
    if (params.storeId) {
      navigate(routeDirectory.partner.orderList.path(params.storeId));
    }
  }

  function handleClickOrder(orderId: string) {
    setSearchParams({
      orderId
    }, {
      replace: true
    });
  }

  function handleClickExportAsnFile() {
    setModalIsOpen('exportAsn', true);
  }

  function handleClickBack() {
    if (queryTuple.data.isSuperAdmin) {
      navigate(routeDirectory.admin.root.path(), {
        replace: true
      });
    } else {
      navigate(routeDirectory.partner.stores.path(), {
        replace: true
      });
    }
  }

  function handleClickDownloadInventoryReport(reportId: string) {
    fnDownloadReport(reportId);
  }

  function handleDeleteReturnAuthorization() {
    setSelectedRecordId(selectedOrderId);
  }

  function handleClickInventoryManagement(storeId: string) {
    navigate(routeDirectory.partner.inventory.path('items', storeId)); // currently we are only allowing items tab for partners
  }


  return !params.storeId ? null : (
    <>
      <activityTemplates.PartnerActivityTemplate.FC
        storeId={params.storeId}
        elLeftContent={(
          <>
            <ButtonBox>
              <IconButton onClick={handleClickBack}>
                <ArrowBack />
              </IconButton>

              <Button
                variant="contained"
                onClick={handleClickOrders}>
                Transactions
              </Button>

              <PermissionWall.FC
                storeId={params.storeId}
                requiresPermissions={['inventory_viewItemList']}>
                <Button
                  variant="contained"
                  onClick={_.partial(handleClickInventoryManagement, params.storeId)}>
                  Inventroy Management
                </Button>
              </PermissionWall.FC>
            </ButtonBox>
          </>
        )}
        elRightContent={(
          <PermissionWall.FC
            storeId={params.storeId}
            requiresPermissions={['reporting_viewReports']}>
            <Stack
              spacing={1}
              direction="row">
              {_.map(queryTuple.data.reports, report => (
                <Button
                  key={report.id}
                  startIcon={<Download />}
                  variant="contained"
                  onClick={_.partial(handleClickDownloadInventoryReport, report.id)}>
                  {report.name}
                </Button>
              ))}

              <Button
                startIcon={<Download />}
                variant="contained"
                onClick={handleClickExportAsnFile}>
                ASN File
              </Button>
            </Stack>
          </PermissionWall.FC>
        )}>

        <ContainerBox>
          <PermissionWall.FC
            storeId={params.storeId}
            requiresPermissions={['salesOrder_view']}>
            <OrderListPane.FC
              selectedOrderId={selectedOrderId}
              storeId={params.storeId ?? ''}
              onClickOrder={handleClickOrder} />
          </PermissionWall.FC>

          {selectedOrderId && (
            <DetailPaneBox>
              <PermissionWall.FC
                storeId={params.storeId}
                requiresPermissions={['salesOrder_view', 'returnAuthorization_view']}>
                <SingleOrderRecordNavigator.FC
                  orderId={selectedOrderId}
                  selectedRecordId={selectedRecordId}
                  onSelectRecord={setSelectedRecordId} />
              </PermissionWall.FC>

              {selectedOrderId === selectedRecordId && selectedRecordId ? (
                <PermissionWall.FC
                  storeId={params.storeId}
                  requiresPermissions={['salesOrder_view']}>
                  <OrderDetailPane.FC
                    storeId={params.storeId ?? ''}
                    orderId={selectedOrderId} />
                </PermissionWall.FC>

              ) : (
                <PermissionWall.FC
                  storeId={params.storeId}
                  requiresPermissions={['returnAuthorization_view']}>
                  <ReturnAuthorizationPane.FC
                    storeId={params.storeId ?? ''}
                    orderId={selectedOrderId}
                    returnAuthorizationId={selectedRecordId}
                    onDeleteReturnAuthorization={handleDeleteReturnAuthorization} />
                </PermissionWall.FC>
              )}
            </DetailPaneBox>
          )}
        </ContainerBox>
      </activityTemplates.PartnerActivityTemplate.FC>

      {modalType2IsOpen.exportAsn && params.storeId && (
        <ExportAsnModal.FC
          storeId={params.storeId}
          onClose={() => setModalIsOpen('exportAsn', false)} />
      )}
    </>
  );
};
